import moment from 'moment';
import T from 'Components/Helpers/translate';
import { hasSoftDeployAccess } from 'App/services/FeatureHelperStandalone';

/**
 * The rows are activities, appointments and mails coming back from one
 * ActivityList query, so what distinguishes them is which fields they carry
 * rather than a discriminant — the same duck-typing the Angular `groupOn`
 * functions did.
 */
type GroupableRow = Record<string, any>;

/**
 * The four options of the Angular contact card's "Group by" select, keyed by the
 * hash it kept in the url (`contactActivities.js:381-435`). Order is the order
 * they were pushed onto `groupable2.data`.
 */
export const GROUP_BY_OPTIONS = ['month', 'year', 'type', 'activeInactive'] as const;

export type GroupBy = (typeof GROUP_BY_OPTIONS)[number];

/** The select's default, and the url default when the hash carries nothing. */
export const DEFAULT_GROUP_BY: GroupBy = 'activeInactive';

export const groupByLabel = (groupBy: GroupBy) =>
	({
		month: T('date.month'),
		year: T('date.year'),
		type: T('default.activityType'),
		activeInactive: T('default.activeOrEnded')
	}[groupBy]);

const isMail = (item: GroupableRow) => 'subject' in item;

/**
 * Angular's `printHistoryOrActivity`: an org that has moved to to-dos and away
 * from activities calls the open group "Open tasks" rather than "Open
 * activities" (`contactActivities.js:353-359`).
 */
const openOrHistory = (isHistory: boolean) => {
	if (isHistory) {
		return T('default.history');
	}
	const hasNoActivities = hasSoftDeployAccess('TODO_LIST') && hasSoftDeployAccess('REMOVE_ACTIVITIES');
	return T(hasNoActivities ? 'default.openTasks' : 'default.openActivities');
};

/**
 * The group an item falls in, as the matching `groupOn` in Angular computed it.
 * The string is the group's identity as well as its heading, which is what
 * `SimpleListView`'s `subHeaderFn` expects.
 */
export const groupOf = (anyItem: GroupableRow, groupBy: GroupBy): string => {
	switch (groupBy) {
		case 'month':
			// Angular's `$date(obj.date, 'MMMM yyyy')`.
			return anyItem.date ? moment(anyItem.date).format('MMMM YYYY') : T('todo.noDate');
		case 'year':
			return anyItem.date ? moment(anyItem.date).format('YYYY') : T('todo.noDate');
		case 'type':
			if (isMail(anyItem)) {
				// `type === 'sch'` is a mail still scheduled to go out.
				return anyItem.type === 'sch'
					? `${T('event.types.Mail')} ${T('mail.scheduled').toLowerCase()}`
					: T('mail.mailOut');
			}
			return anyItem.activityType?.name ?? '';
		case 'activeInactive':
		default:
			if (anyItem.isAppointment) {
				return openOrHistory(moment(anyItem.endDate).diff(moment()) < 0);
			}
			if (isMail(anyItem)) {
				return openOrHistory(anyItem.type !== 'sch');
			}
			return openOrHistory(!!anyItem.closeDate);
	}
};

/**
 * Reorders a date-sorted page so every group's rows sit together, which is what
 * lets `subHeaderFn` emit one heading per group — it starts a new group only
 * where two neighbouring rows differ.
 *
 * Groups come out in the order they are first met rather than sorted, because
 * that is what Angular's `$filter('groupBy')` did over its date-sorted array:
 * the group holding the most recent item leads.
 */
export const orderIntoGroups = <T extends GroupableRow>(items: T[], groupBy: GroupBy): T[] => {
	const buckets = new Map<string, T[]>();

	for (const item of items) {
		const key = groupOf(item, groupBy);
		const bucket = buckets.get(key);
		if (bucket) {
			bucket.push(item);
		} else {
			buckets.set(key, [item]);
		}
	}

	return Array.from(buckets.values()).flat();
};
